/**
 * Desktop notifications: a banner from the browser that shows even while the person is in another
 * application. Unlike sound, it does not need a click on the page first, only a one-time permission.
 */
export const desktopNotificationsSupported = (): boolean => typeof window !== 'undefined' && 'Notification' in window;

export const desktopNotificationPermission = (): NotificationPermission | 'unsupported' =>
  desktopNotificationsSupported() ? Notification.permission : 'unsupported';

let promptInstalled = false;

/**
 * Asks for permission on the person's first click or key press (browsers only show the question
 * after an interaction). Asks once: if they answer or dismiss it, the browser remembers.
 */
export function installDesktopNotificationPrompt(): void {
  if (promptInstalled || !desktopNotificationsSupported() || Notification.permission !== 'default') return;
  promptInstalled = true;
  const ask = () => {
    window.removeEventListener('pointerdown', ask);
    window.removeEventListener('keydown', ask);
    try {
      void Notification.requestPermission();
    } catch {
      // Older browsers use a callback form; nothing to do if it is unavailable.
    }
  };
  window.addEventListener('pointerdown', ask);
  window.addEventListener('keydown', ask);
}

/** True when the person is not looking at this page: another tab, another window or another app. */
export const pageIsInBackground = (): boolean =>
  typeof document !== 'undefined' && (document.visibilityState !== 'visible' || !document.hasFocus());

export type DesktopNotificationInput = {
  title: string;
  body: string;
  /** Same tag replaces an earlier banner instead of stacking. */
  tag?: string;
  /** Called when the banner is clicked, after the window has been brought to the front. */
  onClick?: () => void;
};

/** Shows a banner when permission is granted and the page is in the background. Returns true if shown. */
export function showDesktopNotification({ title, body, tag, onClick }: DesktopNotificationInput): boolean {
  if (!desktopNotificationsSupported() || Notification.permission !== 'granted' || !pageIsInBackground()) return false;
  try {
    const notification = new Notification(title, { body, tag, requireInteraction: false });
    notification.onclick = () => {
      window.focus();
      notification.close();
      onClick?.();
    };
    return true;
  } catch {
    return false;
  }
}
